import * as React from "react";
import { Link } from "@tanstack/react-router";
import { AmbientBackground } from "@/components/landing";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { User, Activity, Bell, Star, Home } from "lucide-react";
import { AppHeader } from "@/pages/shared/components/AppHeader";

export function DashboardContent() {
  const { user } = useAuth();

  const getFirstName = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name.split(" ")[0];
    }
    return user?.email?.split("@")[0] || "there";
  };

  const navLinks = [
    { to: "/", label: "Home", icon: <Home className="mr-2 h-4 w-4" /> },
  ];

  return (
    <div className="relative min-h-screen">
      <AmbientBackground />

      <AppHeader navLinks={navLinks} showSettings />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold">Welcome to your dashboard</h1>
            <p className="text-muted-foreground">
              Hello, {getFirstName()}! Here's an overview of your account.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Activity</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">0</div>
                <p className="text-xs text-muted-foreground">Recent actions</p>
              </CardContent>
            </Card>

            <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Notifications
                </CardTitle>
                <Bell className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">0</div>
                <p className="text-xs text-muted-foreground">Unread messages</p>
              </CardContent>
            </Card>

            <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Status</CardTitle>
                <Star className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-500">Active</div>
                <p className="text-xs text-muted-foreground">
                  Account in good standing
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Getting Started</CardTitle>
              <CardDescription>
                Complete these steps to set up your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-lg border border-border/40 bg-muted/20">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Update your profile</p>
                  <p className="text-sm text-muted-foreground">
                    Add your details and preferences
                  </p>
                </div>
                <Link to="/profile">
                  <Button variant="outline" size="sm">
                    Go to profile
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-lg border border-border/40 bg-muted/20 opacity-60">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Activity className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Explore features</p>
                  <p className="text-sm text-muted-foreground">
                    Discover what you can do
                  </p>
                </div>
                <Button variant="outline" size="sm" disabled>
                  Coming soon
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

export default DashboardContent;
